/**
 * Property Updated Event Component
 *
 * Displays property update events with old → new value format.
 *
 * Display patterns:
 * - Updated (any count): "**Actor** updated **N fields** on this contact" with expandable sub-items
 * - Sub-items show: PropertyName: oldValue → newValue
 */

import { Edit03 as EditIcon } from '@untitledui/icons';
import type { ReactElement, ReactNode } from 'react';
import { useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';

import { EntityBadge } from '@/lib/crm/components/badges/EntityBadge';
import { useCrmObjectSnapshot } from '@/lib/crm/hooks/useCrmObjectSnapshot';
import { useOpenSlideoutOptional } from '@/lib/crm/hooks/useOpenSlideout';
import type { PropertyTypeSchema } from '@/lib/crm/hooks/usePropertySchema';
import { getEntityTypeSentenceLabel } from '@/lib/crm/utils/entityTypeUtils';
import { getPropertyTypeIconComponent } from '@/lib/crm/utils/icons/PropertyTypeIcon';
import { unwrapPropertyValue } from '@/types/crm';

import { getBookingTimelineLabel } from '../booking-labels';
import {
  ActorName,
  bold,
  InlineText,
  PropertyDiffValue,
  TimelineEvent,
} from '../components/shared';
import { SubjectReference } from '../components/SubjectReference';
import { useViewStateLookups } from '../context/TimelineLookupsContext';
import { collectVisibleUpdatedProperties } from '../timelineItemVisibility';
import type { SubItemContent } from '../types/renderer';
import { useEventFormatters } from '../useEventFormatters';
import {
  buildJsonSubItems,
  getCompanionSchema,
} from '../utils/buildJsonSubItems';
import { getTimelineValueText } from '../utils/valueText';
import type { EventComponentProps } from './types';

/**
 * Multi-field updates with more than this many changes collapse the extras
 * behind a "+N more changes" toggle so bulk edits / imports don't flood the
 * timeline. Updates of 3 or fewer fields render in full.
 */
const PROPERTY_UPDATE_COLLAPSE_THRESHOLD = 3;

export function PropertyUpdatedEvent({
  event,
  entityType,
  isContext,
}: EventComponentProps) {
  const { orgId: orgIdParam } = useParams<{ orgId: string }>();
  const orgId = orgIdParam ? Number(orgIdParam) : undefined;
  const { getActorInfo, formatPropertyName, getObjectLabel } =
    useEventFormatters();
  const { lookups } = useViewStateLookups();
  const actor = getActorInfo(event);
  const openSlideout = useOpenSlideoutOptional();
  const bookingLabel = getBookingTimelineLabel(event);

  // Derived before early return so hooks can safely depend on them
  const isAssociatedEvent = 'association_type_name' in event;
  const associatedObject = isAssociatedEvent
    ? lookups.entities[event.entity_id]
    : undefined;
  const isNoteObject = associatedObject?.entity_type_name === 'ahoy_note';

  // The entity's current properties (from the lookups pool), forwarded to
  // PropertyDiffValue so a `formula` field can resolve its target entity type
  // from a sibling property.
  // Read the full entity from the store (kept live via WebSocket) so a formula
  // label can resolve its target type from ahoy_target_entity_type. The light
  // timeline `lookups.entities` projection only carries the display name, so an
  // update event (which changed only one field) can't resolve it otherwise.
  const storeObject = useCrmObjectSnapshot(event.entity_id, orgId);
  const entityProperties =
    storeObject?.properties ?? lookups.entities[event.entity_id]?.properties;

  const handleAssociatedClick = useCallback(() => {
    if (associatedObject) {
      openSlideout?.(associatedObject.entity_id);
    }
  }, [openSlideout, associatedObject]);

  const handleEntityClick = useCallback(
    (entityId: string) => openSlideout?.(entityId),
    [openSlideout]
  );

  const associatedBadgeState = useMemo(
    () =>
      associatedObject && !isNoteObject
        ? {
            kind: 'data' as const,
            data: {
              id: associatedObject.entity_id,
              entityType: associatedObject.entity_type_name,
              displayName: associatedObject.ahoy_display_name ?? 'Unknown',
            },
          }
        : null,
    [associatedObject, isNoteObject]
  );

  // Get property schema from lookups
  const getPropertySchema = (
    propertyName: string
  ): PropertyTypeSchema | undefined => {
    const key = `${event.entity_type_name}.${propertyName}`;
    return lookups.property_types[key];
  };

  // Get icon for a property using schema lookup
  const getPropertyIcon = (propertyName: string): ReactElement => {
    const iconClass = 'w-4 h-4 text-primary';
    const propertySchema = getPropertySchema(propertyName);

    if (propertySchema) {
      const IconComponent = getPropertyTypeIconComponent(propertySchema);
      return <IconComponent className={iconClass} />;
    }

    // Fallback if property not in schema
    return <EditIcon className={iconClass} />;
  };

  // For associated events, get the object label from lookups (for aria-label and fallback)
  // For primary events, use "this {entityType}"
  // Context node → the event's entity is not the record being viewed; use its
  // display name (badged below) instead of "this contact".
  const contextEntity = isContext
    ? lookups.entities[event.entity_id]
    : undefined;
  const objectDisplayText = isAssociatedEvent
    ? getObjectLabel(event.entity_id)
    : (contextEntity?.ahoy_display_name ??
      `this ${getEntityTypeSentenceLabel(entityType)}`);

  let objectDisplay: ReactNode;
  if (associatedBadgeState) {
    objectDisplay = (
      <EntityBadge
        state={associatedBadgeState}
        className='align-middle'
        onClick={openSlideout ? handleAssociatedClick : undefined}
      />
    );
  } else if (isAssociatedEvent) {
    objectDisplay = bold(objectDisplayText);
  } else {
    // Primary node → "this contact"; context node → badge the subject entity.
    objectDisplay = (
      <SubjectReference
        isContext={isContext}
        entityId={event.entity_id}
        fallback={objectDisplayText}
        onEntityClick={openSlideout ? handleEntityClick : undefined}
      />
    );
  }

  // Old property values are in internal_payload with format: {prop_name: {value: ..., ...metadata}}
  const oldPropertyRecords = event.internal_payload?.old_property_values || {};

  // Keep only readable property updates (drops internal / schema-less props).
  const visibleProperties = collectVisibleUpdatedProperties([event], lookups);

  // Booking events use a human-readable label instead of property diffs
  if (bookingLabel !== null) {
    return (
      <TimelineEvent ariaLabel={bookingLabel}>
        <TimelineEvent.Icon event={event} />
        <TimelineEvent.Header timestamp={event.occurred_at}>
          <InlineText>{bold(bookingLabel)}</InlineText>
        </TimelineEvent.Header>
      </TimelineEvent>
    );
  }

  // If all properties are hidden, don't render the event
  if (visibleProperties.length === 0) {
    return null;
  }

  // Build sub-items list for all property updates.
  // JSON properties with a companion schema are decomposed into per-field sub-items.
  const subItems: SubItemContent[] = visibleProperties.flatMap(
    ([propertyName, newValue]) => {
      const schema = getPropertySchema(propertyName);

      // JSON decomposition
      const companionSchema = getCompanionSchema(
        schema,
        event.internal_payload
      );
      if (
        companionSchema &&
        typeof newValue === 'object' &&
        newValue !== null &&
        !Array.isArray(newValue)
      ) {
        return buildJsonSubItems(
          newValue as Record<string, unknown>,
          companionSchema,
          lookups
        );
      }

      const displayName = formatPropertyName(
        propertyName,
        event.entity_type_name
      );
      const isUrl = isUrlProperty(propertyName, newValue);
      const icon = getPropertyIcon(propertyName);
      // Extract the actual value from the old property record
      const oldRecord = oldPropertyRecords[propertyName];
      const oldValue = oldRecord?.value;

      // Render coordinated "before / after" diff. For short values this keeps
      // the compact `old → new` inline layout; for long values it stacks with
      // "Before / After" labels and a single "Show more / Show less" toggle.
      // URL properties skip this entirely — they render as plain links via
      // `href` / `linkText` below (see `!isUrl && valueDisplay` in JSX).
      const valueDisplay: ReactNode = (
        <PropertyDiffValue
          oldValue={oldValue}
          newValue={newValue}
          schema={schema}
          orgId={orgId}
          entityProperties={entityProperties}
        />
      );

      const fullUrl = isUrl ? getFullUrl(newValue) : undefined;

      return {
        icon,
        text: (
          <span>
            {displayName}: {!isUrl && valueDisplay}
          </span>
        ),
        ...(isUrl &&
          fullUrl && {
            href: fullUrl,
            linkText: getTimelineValueText(newValue),
          }),
        ariaLabel: `${displayName}: ${getTimelineValueText(newValue)}`,
      };
    }
  );

  // Use "1 field" (singular) vs "N fields" (plural)
  const propertyCount = visibleProperties.length;
  const fieldLabel =
    propertyCount === 1 ? '1 field' : `${propertyCount} fields`;
  const ariaLabel = `${actor.name} updated ${fieldLabel} on ${objectDisplayText}`;

  return (
    <TimelineEvent ariaLabel={ariaLabel}>
      <TimelineEvent.Icon event={event} />
      <TimelineEvent.Content
        eventId={event.event_id}
        header={
          <InlineText>
            <ActorName actor={actor} /> updated {bold(fieldLabel)} on{' '}
            {objectDisplay}
          </InlineText>
        }
        timestamp={event.occurred_at}
        subItems={subItems}
        subItemsCollapseAfter={PROPERTY_UPDATE_COLLAPSE_THRESHOLD}
      />
    </TimelineEvent>
  );
}

// Helper functions

function isUrlProperty(propertyName: string, value: unknown): boolean {
  const name = propertyName.toLowerCase();
  const urlFields = ['linkedin', 'url', 'website', 'domain'];

  if (!urlFields.some(field => name.includes(field))) {
    return false;
  }

  const strValue = getTimelineValueText(value);
  return strValue.startsWith('http://') || strValue.startsWith('https://');
}

function getFullUrl(value: unknown): string | undefined {
  if (typeof value === 'string') {
    return value.startsWith('http') ? value : undefined;
  }
  // timeline event payload: single-level {value} wrapper — unwrap via unwrapPropertyValue
  const inner = unwrapPropertyValue(value);
  return typeof inner === 'string' && inner.startsWith('http')
    ? inner
    : undefined;
}
